import { useRef } from "react";
import { Download, FileImage, FileText as FilePdf, ArrowRight, CheckCircle2, Play, Flag } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const ROLE_COLORS = {
  AREA_MANAGER:     { bg: "#FEF3C7", border: "#F59E0B", text: "#92400E" },
  SALES_MANAGER:    { bg: "#DBEAFE", border: "#3B82F6", text: "#1E40AF" },
  REGIONAL_MANAGER: { bg: "#E0E7FF", border: "#6366F1", text: "#3730A3" },
  FINANCE_MANAGER:  { bg: "#D1FAE5", border: "#10B981", text: "#065F46" },
  FACTORY_ADMIN:    { bg: "#FED7AA", border: "#F97316", text: "#9A3412" },
  SUPER_ADMIN:      { bg: "#FECACA", border: "#EF4444", text: "#991B1B" },
};

const colorFor = (role) => ROLE_COLORS[role] || { bg: "#F1F5F9", border: "#94A3B8", text: "#334155" };

/**
 * Read-only canvas-like flowchart preview of an approval chain plus PNG/PDF export.
 * Pure SVG so html2canvas / dom-to-image are not required.
 */
export const ApprovalFlowPreview = ({ chain = [], title = "Approval Flow", entity = "Order" }) => {
  const svgRef = useRef(null);

  const NODE_W = 170;
  const NODE_H = 80;
  const GAP = 50;
  const PAD = 30;
  const Y = 60;

  const nodes = [
    { kind: "start", label: "Submitted" },
    ...chain.map((s) => ({ kind: "step", ...s })),
    { kind: "end", label: "Final Approval" },
  ];

  const totalW = PAD * 2 + nodes.length * NODE_W + (nodes.length - 1) * GAP;
  const totalH = NODE_H + Y + PAD;

  const exportSVGAsPNG = async () => {
    try {
      const svg = svgRef.current;
      const svgData = new XMLSerializer().serializeToString(svg);
      const svgBlob = new Blob([svgData], { type: "image/svg+xml;charset=utf-8" });
      const url = URL.createObjectURL(svgBlob);

      const img = new Image();
      img.crossOrigin = "anonymous";
      await new Promise((res, rej) => {
        img.onload = res;
        img.onerror = rej;
        img.src = url;
      });

      const canvas = document.createElement("canvas");
      const scale = 2;
      canvas.width = totalW * scale;
      canvas.height = totalH * scale;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#FFFFFF";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.scale(scale, scale);
      ctx.drawImage(img, 0, 0);
      URL.revokeObjectURL(url);

      canvas.toBlob((blob) => {
        const dl = document.createElement("a");
        dl.href = URL.createObjectURL(blob);
        dl.download = `${entity.toLowerCase()}-approval-flow.png`;
        dl.click();
        URL.revokeObjectURL(dl.href);
        toast.success("Flow exported as PNG");
      }, "image/png");
    } catch (e) {
      toast.error("PNG export failed");
    }
  };

  const exportAsPDF = async () => {
    try {
      const svg = svgRef.current;
      const svgData = new XMLSerializer().serializeToString(svg);
      const html = `<!doctype html><html><head><title>${entity} Approval Flow</title>
        <style>
          body{font-family:-apple-system,Segoe UI,Roboto,Helvetica,sans-serif;margin:32px;color:#0F172A}
          h1{margin:0 0 4px;font-size:22px;color:#8B1A1A}
          .meta{color:#64748B;font-size:12px;margin-bottom:24px}
          .legend{margin-top:24px;font-size:11px;color:#475569;display:flex;gap:14px;flex-wrap:wrap}
          .legend span{display:inline-flex;align-items:center;gap:4px}
          .dot{display:inline-block;width:10px;height:10px;border-radius:9999px}
          @media print{ button{display:none} }
        </style></head><body>
        <h1>${title}</h1>
        <div class="meta">${entity} approval workflow · Pravaah · Avi Cement Limited · Generated ${new Date().toLocaleString()}</div>
        ${svgData}
        <div class="legend">
          ${Object.entries(ROLE_COLORS)
            .map(([r, c]) => `<span><span class="dot" style="background:${c.border}"></span>${r.replace(/_/g, " ")}</span>`)
            .join("")}
        </div>
        <button onclick="window.print()" style="margin-top:24px;padding:8px 14px;background:#8B1A1A;color:#fff;border:none;border-radius:6px;cursor:pointer">Print / Save as PDF</button>
        <script>setTimeout(()=>window.print(),350)</script>
      </body></html>`;
      const w = window.open("", "_blank");
      if (!w) { toast.error("Pop-up blocked - allow pop-ups to export PDF"); return; }
      w.document.write(html);
      w.document.close();
      toast.success("Print dialog opened — choose 'Save as PDF'");
    } catch {
      toast.error("PDF export failed");
    }
  };

  if (chain.length === 0) {
    return (
      <div className="border border-dashed dark:border-slate-700 rounded-md py-6 px-4 text-center" data-testid="approval-flow-preview-empty">
        <p className="text-xs text-slate-400 italic">
          Add at least one step above to see the visual flow preview.
        </p>
      </div>
    );
  }

  return (
    <div className="border border-slate-200 dark:border-slate-700 rounded-md bg-white dark:bg-slate-900 overflow-hidden" data-testid="approval-flow-preview">
      {/* Header bar */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-emerald-500" />
          <p className="text-xs font-semibold text-slate-700 dark:text-slate-200">
            {title} <span className="text-slate-400 font-normal">· {chain.length} step{chain.length === 1 ? "" : "s"}</span>
          </p>
        </div>
        <div className="flex items-center gap-1">
          <Button size="sm" variant="outline" onClick={exportSVGAsPNG} className="h-7 text-[10px]" data-testid="export-flow-png">
            <FileImage className="w-3 h-3 mr-1" /> PNG
          </Button>
          <Button size="sm" variant="outline" onClick={exportAsPDF} className="h-7 text-[10px]" data-testid="export-flow-pdf">
            <FilePdf className="w-3 h-3 mr-1" /> PDF
          </Button>
        </div>
      </div>

      {/* Canvas */}
      <div className="overflow-x-auto p-3 bg-gradient-to-br from-slate-50 to-white dark:from-slate-900 dark:to-slate-950">
        <svg
          ref={svgRef}
          width={totalW}
          height={totalH}
          viewBox={`0 0 ${totalW} ${totalH}`}
          xmlns="http://www.w3.org/2000/svg"
          style={{ display: "block" }}
        >
          <defs>
            <marker
              id="arrow"
              viewBox="0 0 10 10"
              refX="8"
              refY="5"
              markerWidth="6"
              markerHeight="6"
              orient="auto-start-reverse"
            >
              <path d="M 0 0 L 10 5 L 0 10 z" fill="#94A3B8" />
            </marker>
            <pattern id="grid" width="20" height="20" patternUnits="userSpaceOnUse">
              <path d="M 20 0 L 0 0 0 20" fill="none" stroke="#F1F5F9" strokeWidth="0.5" />
            </pattern>
          </defs>
          <rect width={totalW} height={totalH} fill="url(#grid)" />

          {nodes.map((n, i) => {
            const x = PAD + i * (NODE_W + GAP);
            const isStart = n.kind === "start";
            const isEnd = n.kind === "end";
            const role = n.role || (isStart ? "START" : isEnd ? "END" : "STEP");
            const c = isStart
              ? { bg: "#ECFDF5", border: "#10B981", text: "#065F46" }
              : isEnd
              ? { bg: "#FEF2F2", border: "#8B1A1A", text: "#7F1D1D" }
              : colorFor(n.role);

            return (
              <g key={i}>
                {/* Connector line + arrow to next */}
                {i < nodes.length - 1 && (
                  <line
                    x1={x + NODE_W}
                    y1={Y + NODE_H / 2}
                    x2={x + NODE_W + GAP - 2}
                    y2={Y + NODE_H / 2}
                    stroke="#94A3B8"
                    strokeWidth="2"
                    strokeDasharray={isStart ? "0" : "0"}
                    markerEnd="url(#arrow)"
                  />
                )}

                {/* Node */}
                <rect
                  x={x}
                  y={Y}
                  width={NODE_W}
                  height={NODE_H}
                  rx={isStart || isEnd ? 40 : 8}
                  fill={c.bg}
                  stroke={c.border}
                  strokeWidth="2"
                />

                {/* Step number badge */}
                {!isStart && !isEnd && (
                  <>
                    <circle cx={x + 16} cy={Y + 16} r="11" fill={c.border} />
                    <text
                      x={x + 16}
                      y={Y + 20}
                      textAnchor="middle"
                      fontSize="11"
                      fontWeight="700"
                      fill="#FFFFFF"
                    >
                      {n.step || i}
                    </text>
                  </>
                )}

                {/* Icon */}
                <text
                  x={x + NODE_W / 2}
                  y={Y + 30}
                  textAnchor="middle"
                  fontSize="16"
                >
                  {isStart ? "▶" : isEnd ? "🏁" : "✓"}
                </text>

                {/* Role / label */}
                <text
                  x={x + NODE_W / 2}
                  y={Y + 50}
                  textAnchor="middle"
                  fontSize="11"
                  fontWeight="700"
                  fill={c.text}
                >
                  {(role || "").replace(/_/g, " ")}
                </text>
                <text
                  x={x + NODE_W / 2}
                  y={Y + 66}
                  textAnchor="middle"
                  fontSize="9"
                  fill={c.text}
                  opacity="0.85"
                >
                  {(n.label || (isStart ? `${entity} submitted` : "Approved")).slice(0, 26)}
                </text>
              </g>
            );
          })}

          {/* Title */}
          <text x={PAD} y={28} fontSize="12" fontWeight="700" fill="#0F172A">
            {entity} Approval Flow
          </text>
          <text x={PAD} y={44} fontSize="10" fill="#64748B">
            {chain.length} approval step{chain.length === 1 ? "" : "s"} · auto-routes after each approval
          </text>
        </svg>
      </div>
    </div>
  );
};

export default ApprovalFlowPreview;
